<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div class="container-custom py-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="goBack"
              class="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              返回
            </button>
            <h1 class="text-2xl font-bold text-gray-900">在線估價系統</h1>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container-custom py-8">
      <!-- Quote Form -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">選擇產品與數量</h2>

        <!-- Loading State -->
        <div v-if="isLoadingProducts" class="flex justify-center items-center py-12">
          <div class="spinner w-8 h-8"></div>
          <span class="ml-3 text-gray-600">載入產品列表中...</span>
        </div>

        <!-- Product Selection Items -->
        <div v-else class="space-y-4">
          <div
            v-for="(item, index) in selectedItems"
            :key="item.id"
            class="product-item border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
          >
            <!-- Desktop Layout -->
            <div class="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <!-- NO. -->
              <div class="col-span-1">
                <span class="text-sm font-semibold text-gray-700">NO.{{ index + 1 }}</span>
              </div>

              <!-- Product Category (Fixed) -->
              <div class="col-span-2">
                <div class="text-sm text-gray-600">產品類別</div>
                <div class="font-medium text-gray-900">Shade Kite®</div>
              </div>

              <!-- Product Select -->
              <div class="col-span-4">
                <label class="block text-sm text-gray-600 mb-1">選擇規格</label>
                <select
                  v-model="item.productId"
                  @change="selectProduct(item.id, item.productId)"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">請選擇規格...</option>
                  <option
                    v-for="(product, index) in availableProducts"
                    :key="product.id"
                    :value="product.id"
                  >
                    產品編號 {{ index + 1 }} | 規格 {{ product.spec }}cm | 斜邊長 {{ product.diagonal }}cm | 面積 {{ product.areaM2 }}m² | 面積 {{ product.areaPing }}坪 | 邊帶寬度 {{ product.edgeBand }}mm | ${{ product.unitPrice.toLocaleString() }}
                  </option>
                </select>
              </div>

              <!-- Quantity -->
              <div class="col-span-2">
                <label class="block text-sm text-gray-600 mb-1">數量</label>
                <input
                  type="number"
                  v-model.number="item.quantity"
                  @input="updateQuantity(item.id, item.quantity)"
                  min="1"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  :disabled="!item.product"
                  required
                >
              </div>

              <!-- Subtotal -->
              <div class="col-span-2">
                <label class="block text-sm text-gray-600 mb-1">小計</label>
                <div class="font-bold text-lg text-primary-600">
                  ${{ item.subtotal.toLocaleString() }}
                </div>
              </div>

              <!-- Delete Button -->
              <div class="col-span-1 flex justify-end">
                <button
                  @click="removeItem(item.id)"
                  :disabled="selectedItems.length === 1"
                  class="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="刪除此項目"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Mobile Layout -->
            <div class="md:hidden space-y-3">
              <!-- Header with NO. and Delete -->
              <div class="flex items-center justify-between">
                <span class="text-sm font-semibold text-gray-700">NO.{{ index + 1 }}</span>
                <button
                  @click="removeItem(item.id)"
                  :disabled="selectedItems.length === 1"
                  class="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="刪除此項目"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>

              <!-- Product Category -->
              <div>
                <div class="text-sm text-gray-600">產品類別</div>
                <div class="font-medium text-gray-900">Shade Kite® 張力三角遮陽</div>
              </div>

              <!-- Product Select -->
              <div>
                <label class="block text-sm text-gray-600 mb-1">選擇規格</label>
                <select
                  v-model="item.productId"
                  @change="selectProduct(item.id, item.productId)"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">請選擇規格...</option>
                  <option
                    v-for="(product, index) in availableProducts"
                    :key="product.id"
                    :value="product.id"
                  >
                    產品編號 {{ index + 1 }} | 規格 {{ product.spec }}cm | 斜邊長 {{ product.diagonal }}cm | 面積 {{ product.areaM2 }}m² | 面積 {{ product.areaPing }}坪 | 邊帶寬度 {{ product.edgeBand }}mm | ${{ product.unitPrice.toLocaleString() }}
                  </option>
                </select>
              </div>

              <!-- Quantity and Subtotal -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm text-gray-600 mb-1">數量</label>
                  <input
                    type="number"
                    v-model.number="item.quantity"
                    @input="updateQuantity(item.id, item.quantity)"
                    min="1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    :disabled="!item.product"
                    required
                  >
                </div>
                <div>
                  <label class="block text-sm text-gray-600 mb-1">小計</label>
                  <div class="font-bold text-lg text-primary-600 py-2">
                    ${{ item.subtotal.toLocaleString() }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Product Button -->
          <button
            @click="addNewItem"
            class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            <span class="font-medium">新增產品</span>
          </button>

          <!-- Total Section -->
          <div class="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6 mt-6">
            <div class="flex justify-between items-center">
              <div>
                <div class="text-sm text-gray-600 mb-1">總計金額</div>
                <div class="text-3xl font-bold text-primary-600">
                  NT$ {{ totalAmount.toLocaleString() }}
                </div>
              </div>
              <div class="text-right text-sm text-gray-600">
                <div>已選擇 {{ selectedProductCount }} 項產品</div>
                <div>總數量 {{ totalQuantity }} 件</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Customer Information Form -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">聯絡資訊</h2>

        <form @submit.prevent="submitQuote" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- First Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                名字 *
              </label>
              <input
                v-model="customerInfo.firstName"
                type="text"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="請輸入您的名字"
              >
            </div>

            <!-- Last Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                姓氏 *
              </label>
              <input
                v-model="customerInfo.lastName"
                type="text"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="請輸入您的姓氏"
              >
            </div>

            <!-- Email -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                電子郵件 *
              </label>
              <input
                v-model="customerInfo.email"
                type="email"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="your.email@example.com"
              >
            </div>

            <!-- Phone -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                手機號碼 *
              </label>
              <input
                v-model="customerInfo.phone"
                type="tel"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0912-345-678"
              >
            </div>
          </div>

          <!-- Message -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              備註 (選填)
            </label>
            <textarea
              v-model="customerInfo.message"
              rows="4"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="請輸入任何特殊需求或備註..."
            ></textarea>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="isSubmitting || !isFormValid"
              class="px-8 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="!isSubmitting">提交估價單</span>
              <span v-else class="flex items-center space-x-2">
                <div class="spinner w-4 h-4"></div>
                <span>提交中...</span>
              </span>
            </button>
          </div>
        </form>
      </div>

      <!-- Success Message -->
      <div
        v-if="showSuccessMessage"
        class="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3"
      >
        <svg class="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <h4 class="font-semibold text-green-900">估價單提交成功！</h4>
          <p class="text-sm text-green-700 mt-1">我們會盡快與您聯繫，提供詳細報價。</p>
        </div>
      </div>

      <!-- Error Message -->
      <div
        v-if="showErrorMessage"
        class="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
      >
        <svg class="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <h4 class="font-semibold text-red-900">提交失敗</h4>
          <p class="text-sm text-red-700 mt-1">{{ errorMessage || '請稍後再試或直接聯絡我們。' }}</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore'
import { db } from '@/config/firebase'

export default {
  name: 'Quote',
  setup() {
    const router = useRouter()

    // Available products from database
    const availableProducts = ref([])
    const isLoadingProducts = ref(true)

    // Selected items (dynamic array)
    const selectedItems = ref([])

    // Customer information
    const customerInfo = ref({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: ''
    })

    // UI states
    const isSubmitting = ref(false)
    const showSuccessMessage = ref(false)
    const showErrorMessage = ref(false)
    const errorMessage = ref('')

    // Generate unique ID for items
    const generateUniqueId = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Computed properties
    const totalAmount = computed(() => {
      return selectedItems.value.reduce((total, item) => total + item.subtotal, 0)
    })

    const selectedProductCount = computed(() => {
      return selectedItems.value.filter(item => item.product !== null).length
    })

    const totalQuantity = computed(() => {
      return selectedItems.value.reduce((total, item) => item.quantity, 0)
    })

    const isFormValid = computed(() => {
      // At least one product must be selected with quantity > 0
      const hasValidProducts = selectedItems.value.some(item => item.product && item.quantity > 0)
      return hasValidProducts && totalAmount.value > 0
    })

    // Methods
    const goBack = () => {
      router.push('/')
    }

    // Load available products from Firebase
    const loadAvailableProducts = async () => {
      try {
        isLoadingProducts.value = true

        // Try Firebase first
        try {
          const quoteItemsRef = collection(db, 'quoteItems')
          const q = query(
            quoteItemsRef,
            where('enabled', '==', true),
            orderBy('code', 'asc')
          )
          const querySnapshot = await getDocs(q)

          if (!querySnapshot.empty) {
            availableProducts.value = querySnapshot.docs.map(doc => {
              const data = doc.data()
              return {
                id: doc.id,
                code: data.code,
                spec: data.spec,
                diagonal: data.diagonal,
                areaM2: data.areaM2,
                areaPing: data.areaPing,
                edgeBand: data.edgeBand,
                unitPrice: data.unitPrice
              }
            })
            console.log('Loaded available products from Firebase:', availableProducts.value.length)
            return
          }
        } catch (firebaseError) {
          console.warn('Firebase load failed, trying backend API:', firebaseError.message)
        }

        // Fallback to backend API
        const response = await fetch('http://139.180.210.187/api/admin/quote-items')
        if (response.ok) {
          const result = await response.json()
          if (result.success && result.data) {
            availableProducts.value = result.data
              .filter(item => item.enabled !== false)
              .map(item => ({
                id: item.id,
                code: item.code,
                spec: item.spec,
                diagonal: item.diagonal,
                areaM2: item.areaM2,
                areaPing: item.areaPing,
                edgeBand: item.edgeBand,
                unitPrice: item.unitPrice
              }))
            console.log('Loaded available products from API:', availableProducts.value.length)
            return
          }
        }

        // Final fallback to demo data
        console.warn('All data sources failed, using demo data')
        loadDemoProducts()
      } catch (error) {
        console.error('Error loading products:', error)
        loadDemoProducts()
      } finally {
        isLoadingProducts.value = false
      }
    }

    // Load demo products for development
    const loadDemoProducts = () => {
      availableProducts.value = [
        {
          id: '1',
          code: 'SK-200-200',
          spec: '200×200',
          diagonal: 283,
          areaM2: 4.0,
          areaPing: 1.2,
          edgeBand: 20,
          unitPrice: 12000
        },
        {
          id: '2',
          code: 'SK-250-250',
          spec: '250×250',
          diagonal: 354,
          areaM2: 6.25,
          areaPing: 1.9,
          edgeBand: 20,
          unitPrice: 18000
        },
        {
          id: '3',
          code: 'SK-300-300',
          spec: '300×300',
          diagonal: 424,
          areaM2: 9.0,
          areaPing: 2.7,
          edgeBand: 30,
          unitPrice: 25000
        }
      ]
    }

    // Add a new item to selection
    const addNewItem = () => {
      selectedItems.value.push({
        id: generateUniqueId(),
        productId: '',
        product: null,
        quantity: 1,
        subtotal: 0
      })
    }

    // Remove an item from selection
    const removeItem = (itemId) => {
      if (selectedItems.value.length > 1) {
        selectedItems.value = selectedItems.value.filter(item => item.id !== itemId)
      }
    }

    // Select a product for an item
    const selectProduct = (itemId, productId) => {
      const item = selectedItems.value.find(i => i.id === itemId)
      const product = availableProducts.value.find(p => p.id === productId)

      if (item) {
        if (product) {
          item.product = product
          item.subtotal = product.unitPrice * item.quantity
        } else {
          item.product = null
          item.subtotal = 0
        }
      }
    }

    // Update quantity for an item
    const updateQuantity = (itemId, quantity) => {
      const item = selectedItems.value.find(i => i.id === itemId)
      if (item && item.product) {
        item.quantity = Math.max(1, parseInt(quantity) || 1)
        item.subtotal = item.product.unitPrice * item.quantity
      }
    }

    // Submit quote
    const submitQuote = async () => {
      try {
        isSubmitting.value = true
        showSuccessMessage.value = false
        showErrorMessage.value = false
        errorMessage.value = ''

        // Validate that at least one product is selected
        const validItems = selectedItems.value.filter(item => item.product && item.quantity > 0)
        if (validItems.length === 0) {
          errorMessage.value = '請至少選擇一個產品'
          showErrorMessage.value = true
          return
        }

        // Prepare quote data
        const quoteData = {
          customerInfo: customerInfo.value,
          items: validItems.map(item => ({
            code: item.product.code,
            spec: item.product.spec,
            diagonal: item.product.diagonal,
            areaM2: item.product.areaM2,
            areaPing: item.product.areaPing,
            edgeBand: item.product.edgeBand,
            unitPrice: item.product.unitPrice,
            quantity: item.quantity,
            subtotal: item.subtotal
          })),
          totalAmount: totalAmount.value,
          createdAt: new Date().toISOString()
        }

        console.log('Submitting quote:', quoteData)

        // Submit to backend
        const response = await fetch('http://139.180.210.187/api/quote', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(quoteData)
        })

        if (!response.ok) {
          throw new Error('提交失敗')
        }

        const result = await response.json()
        console.log('Quote submitted successfully:', result)

        // Show success message
        showSuccessMessage.value = true

        // Reset form
        resetForm()

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' })

      } catch (error) {
        console.error('Error submitting quote:', error)
        errorMessage.value = error.message || '提交失敗，請稍後再試'
        showErrorMessage.value = true
      } finally {
        isSubmitting.value = false
      }
    }

    // Reset form
    const resetForm = () => {
      // Reset to one empty item
      selectedItems.value = [{
        id: generateUniqueId(),
        productId: '',
        product: null,
        quantity: 1,
        subtotal: 0
      }]

      // Reset customer info
      customerInfo.value = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      }
    }

    // Initialize
    onMounted(async () => {
      await loadAvailableProducts()
      // Add one empty item to start
      addNewItem()
    })

    return {
      availableProducts,
      selectedItems,
      customerInfo,
      isSubmitting,
      showSuccessMessage,
      showErrorMessage,
      errorMessage,
      totalAmount,
      selectedProductCount,
      totalQuantity,
      isLoadingProducts,
      isFormValid,
      goBack,
      addNewItem,
      removeItem,
      selectProduct,
      updateQuantity,
      submitQuote
    }
  }
}
</script>

<style scoped>
.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.product-item {
  transition: all 0.2s ease;
}

.product-item:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* Input focus styles */
input:focus,
select:focus,
textarea:focus {
  outline: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container-custom {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Custom scrollbar for select */
select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}
</style>